/**
 * Typed client for the marketplace-api voting backend, public-site
 * variant.
 *
 * This file mirrors the dashboard's `lib/marketplace-api.ts` but
 * with two differences:
 *   1. No daemon — auth uses SIWE (browser wallet via wagmi-style
 *      window.ethereum, OR a deeplink to the user's local Axon
 *      daemon if it's running).
 *   2. No demo mode — the public site is always live; if the
 *      backend isn't reachable, we render a "votes unavailable"
 *      state.
 *
 * For v1.2 the public site reads scores (cheap, no auth) but
 * defers the SIWE write path. The "Upvote" button on the public
 * site offers a CTA: open the local daemon dashboard's marketplace
 * tab and vote there. Tracking the SIWE-fallback fully is a v1.3
 * polish.
 */

export const MARKETPLACE_API_URL: string =
  (typeof window !== "undefined" &&
    (window as unknown as { AXON_MARKETPLACE_API_URL?: string }).AXON_MARKETPLACE_API_URL) ||
  // Vite injects env at build time via import.meta.env.VITE_*.
  ((import.meta as unknown as { env?: { VITE_MARKETPLACE_API_URL?: string } }).env?.VITE_MARKETPLACE_API_URL) ||
  "https://axon-marketplace-api.vercel.app";

export interface AggregateScore {
  ok: true;
  strategyId: string;
  rawScore: number;
  weightedScore: number;
  upvotes: number;
  downvotes: number;
  lastVoteAt: number | null;
  /** Anonymous upvote count from the friction-free counter. */
  anonUpvotes?: number;
}

/** Read aggregate score for a strategy. Public — no auth. */
export async function getScore(strategyId: string): Promise<AggregateScore> {
  const res = await fetch(
    `${MARKETPLACE_API_URL}/v1/votes/${encodeURIComponent(strategyId)}`,
    {
      headers: { Accept: "application/json" },
      // Don't send credentials on read paths — keeps the response
      // CDN-cacheable (some CDNs refuse to cache `Vary: Cookie`).
      cache: "no-store",
    },
  );
  if (!res.ok) throw new Error(`getScore failed: ${res.status}`);
  return (await res.json()) as AggregateScore;
}

/** Anonymous fire-and-forget upvote — no auth, no daemon, no
 *  passphrase. The server buckets by (strategy, ip_hash, UTC day)
 *  so a single IP can only contribute one upvote per day per
 *  strategy. */
export async function castUpvote(strategyId: string): Promise<{ ok: true; strategyId: string; upvotes: number; newInsert: boolean }> {
  const res = await fetch(
    `${MARKETPLACE_API_URL}/v1/votes/up/${encodeURIComponent(strategyId)}`,
    {
      method: "POST",
      headers: { Accept: "application/json" },
    },
  );
  if (!res.ok) throw new Error(`castUpvote failed: ${res.status}`);
  return (await res.json()) as { ok: true; strategyId: string; upvotes: number; newInsert: boolean };
}
